import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  API_BASE_URL, VISA_DETAIL_TABS, VISA_DETAIL_COLUMNS, VISA_DETAIL_MESSAGES, getSubmitButtonLabel,
  getContentUpdateDescription, getSheetUrl, VISA_API_BASE_URL,
  VISA_DETAIL_DUPLICATE_CHECK, findDuplicates,
} from "./constants/adminConstants";
import "./css/adminStyles.css";

// ── Scrollable table wrapper with left/right scroll buttons ────────────

function TableWithScrollButtons({ children }) {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      setShowLeft(el.scrollLeft > 5);
      setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
      ro.disconnect();
    };
  }, [checkScroll]);

  const scrollTo = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir === "left" ? -300 : 300, behavior: "smooth" });
  };

  return (
    <div className="AdminTableScrollWrap">
      <div className="AdminScrollBar">
          <button className="AdminScrollBtn" onClick={() => scrollTo("left")} disabled={!showLeft} title="Scroll left">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="10 4 6 8 10 12" />
            </svg>
          </button>
          <button className="AdminScrollBtn" onClick={() => scrollTo("right")} disabled={!showRight} title="Scroll right">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 4 10 8 6 12" />
            </svg>
          </button>
        </div>
      <div ref={scrollRef} className="AdminTableScroll">
        {children}
      </div>
    </div>
  );
}

function AdminVisaDetailEditor({ user, section, onBack }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("resources");
  const [sectionInfo, setSectionInfo] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [successTitle, setSuccessTitle] = useState("");
  const [arraySearch, setArraySearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const [stateSearchOpen, setStateSearchOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [countrySearchOpen, setCountrySearchOpen] = useState(false);
  const [visaOfficeTypeSearch, setVisaOfficeTypeSearch] = useState("");
  const [visaOfficeTypeSearchOpen, setVisaOfficeTypeSearchOpen] = useState(false);
  const [embassyTypeSearch, setEmbassyTypeSearch] = useState("");
  const [embassyTypeSearchOpen, setEmbassyTypeSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const stateSearchRef = useRef(null);
  const countrySearchRef = useRef(null);
  const visaOfficeTypeSearchRef = useRef(null);
  const embassyTypeSearchRef = useRef(null);
  const [linkSearch, setLinkSearch] = useState({});
  const [linkSearchOpen, setLinkSearchOpen] = useState({});
  const linkSearchRefs = useRef({});
  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);
  useEffect(() => {
    if (stateSearchOpen && stateSearchRef.current) {
      stateSearchRef.current.focus();
    }
  }, [stateSearchOpen]);
  useEffect(() => {
    if (countrySearchOpen && countrySearchRef.current) {
      countrySearchRef.current.focus();
    }
  }, [countrySearchOpen]);
  useEffect(() => {
    if (visaOfficeTypeSearchOpen && visaOfficeTypeSearchRef.current) {
      visaOfficeTypeSearchRef.current.focus();
    }
  }, [visaOfficeTypeSearchOpen]);
  useEffect(() => {
    if (embassyTypeSearchOpen && embassyTypeSearchRef.current) {
      embassyTypeSearchRef.current.focus();
    }
  }, [embassyTypeSearchOpen]);

  useEffect(() => {
    const openIdx = Object.keys(linkSearchOpen).find((id) => linkSearchOpen[id]);
    if (openIdx && linkSearchRefs.current[openIdx]) {
      linkSearchRefs.current[openIdx].focus();
    }
  }, [linkSearchOpen]);

  useEffect(() => {
    setArraySearch("");
    setSearchOpen(false);
    setStateSearch("");
    setStateSearchOpen(false);
    setCountrySearch("");
    setCountrySearchOpen(false);
    setVisaOfficeTypeSearch("");
    setVisaOfficeTypeSearchOpen(false);
    setEmbassyTypeSearch("");
    setEmbassyTypeSearchOpen(false);
    setLinkSearch({});
    setLinkSearchOpen({});
  }, [activeTab]);
  const tabBarRef = useRef(null);
  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    const wheelHandler = (e) => {
      if (e.deltaY !== 0) { e.preventDefault(); el.scrollLeft += e.deltaY; }
    };
    el.addEventListener("wheel", wheelHandler, { passive: false });
    return () => el.removeEventListener("wheel", wheelHandler);
  }, []);
  const tabDragRef = useRef(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState([]);
  const [duplicateActionLoading, setDuplicateActionLoading] = useState(false);
  // Row form modal state
  const [showRowForm, setShowRowForm] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null); // null = new row, number = editing
  const [editingItem, setEditingItem] = useState({});
  const [tagInputValue, setTagInputValue] = useState("");
  const [insertAtPosition, setInsertAtPosition] = useState(null);
  const snapshotRef = useRef(null);
  const dragRef = useRef(null);

  const computeStates = useCallback((c) => {
    if (!c) return [];
    const nameSet = new Set();
    const arr = [];
    for (const o of (c.visaOffices || [])) {
      if (o.state && !nameSet.has(o.state)) {
        nameSet.add(o.state);
        arr.push({ name: o.state });
      }
    }
    for (const e of (c.embassies || [])) {
      if (e.state && !nameSet.has(e.state)) {
        nameSet.add(e.state);
        arr.push({ name: e.state });
      }
    }
    return arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, []);

  const computeCountries = useCallback((c) => {
    if (!c) return [];
    const nameSet = new Set();
    const arr = [];
    for (const e of (c.embassies || [])) {
      if (e.country && !nameSet.has(e.country)) {
        nameSet.add(e.country);
        arr.push({ name: e.country });
      }
    }
    for (const f of (c.visaForms || [])) {
      if (f.country && !nameSet.has(f.country)) {
        nameSet.add(f.country);
        arr.push({ name: f.country });
      }
    }
    return arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, []);

  const computeVisaOfficeTypes = useCallback((c) => {
    if (!c) return [];
    const nameSet = new Set();
    const arr = [];
    for (const o of (c.visaOffices || [])) {
      if (o.office_type && !nameSet.has(o.office_type)) {
        nameSet.add(o.office_type);
        arr.push({ name: o.office_type });
      }
    }
    return arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, []);

  const computeEmbassyTypes = useCallback((c) => {
    if (!c) return [];
    const nameSet = new Set();
    const arr = [];
    for (const e of (c.embassies || [])) {
      if (e.embassy_type && !nameSet.has(e.embassy_type)) {
        nameSet.add(e.embassy_type);
        arr.push({ name: e.embassy_type });
      }
    }
    return arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, []);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data && data.section) {
        let contentData = data.section.content;

        // Auto-fetch from CRUD tables if the arrays are empty
        const needsOffices = !contentData.visaOffices || contentData.visaOffices.length === 0;
        const needsEmbassies = !contentData.embassies || contentData.embassies.length === 0;
        const needsForms = !contentData.visaForms || contentData.visaForms.length === 0;

        if (needsOffices || needsEmbassies || needsForms) {
          try {
            const [officesRes, embassiesRes, formsRes] = await Promise.all([
              needsOffices ? fetch(`${VISA_API_BASE_URL}/offices`).then(r => r.ok ? r.json() : { data: [] }) : Promise.resolve({ data: [] }),
              needsEmbassies ? fetch(`${VISA_API_BASE_URL}/embassies`).then(r => r.ok ? r.json() : { data: [] }) : Promise.resolve({ data: [] }),
              needsForms ? fetch(`${VISA_API_BASE_URL}/forms`).then(r => r.ok ? r.json() : { data: [] }) : Promise.resolve({ data: [] }),
            ]);

            // API response is { success, data } — extract the data array
            const parseResponse = (res) => {
              if (Array.isArray(res)) return res;
              if (res?.data && Array.isArray(res.data)) return res.data;
              return [];
            };

            const offices = parseResponse(officesRes);
            const embassies = parseResponse(embassiesRes);
            const forms = parseResponse(formsRes);

            if (offices.length > 0 || embassies.length > 0 || forms.length > 0) {
              contentData = {
                ...contentData,
                ...(offices.length > 0 ? { visaOffices: offices } : {}),
                ...(embassies.length > 0 ? { embassies } : {}),
                ...(forms.length > 0 ? { visaForms: forms } : {}),
              };
            }
          } catch (fetchErr) {
            console.warn("[VisaDetailEditor] Failed to fetch CRUD data:", fetchErr.message);
          }
        }

        // Populate states from computed data if not already set
        if (!contentData.states || contentData.states.length === 0) {
          contentData.states = computeStates(contentData);
        }

        // Populate countries from computed data, then merge with full country list from API
        if (!contentData.countries || contentData.countries.length === 0) {
          contentData.countries = computeCountries(contentData);
        }
        try {
          const countriesRes = await fetch(`${API_BASE_URL}/countries`, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem("tvc_token")}` },
          });
          if (countriesRes.ok) {
            const countriesData = await countriesRes.json();
            const allCountries = (countriesData.countries || []).map((c) => ({ name: c.name }));
            const existingNames = new Set(contentData.countries.map((c) => c.name));
            for (const c of allCountries) {
              if (!existingNames.has(c.name)) {
                existingNames.add(c.name);
                contentData.countries.push(c);
              }
            }
          }
        } catch (fetchErr) {
          console.warn("[VisaDetailEditor] Failed to fetch countries:", fetchErr.message);
        }
        contentData.countries.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        // Populate types from computed data if not already set
        if (!contentData.visaOfficeTypes || contentData.visaOfficeTypes.length === 0) {
          contentData.visaOfficeTypes = computeVisaOfficeTypes(contentData);
        }
        if (!contentData.embassyTypes || contentData.embassyTypes.length === 0) {
          contentData.embassyTypes = computeEmbassyTypes(contentData);
        }
        // Clean up old location field if present
        delete contentData.location;
        setContent(contentData);
        snapshotRef.current = JSON.stringify(contentData);
        setSectionInfo({ version: data.section.version, updatedAt: data.section.updatedAt });
      } else {
        setMessage(VISA_DETAIL_MESSAGES.apiError);
      }
    } catch (err) {
      setMessage(VISA_DETAIL_MESSAGES.connectionError);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSheet = () => {
    window.open(getSheetUrl(section.key), "_blank", "noopener,noreferrer");
  };

  const getSheetId = (url) => {
    const m = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
    return m ? m[1] : url;
  };

  const handlePushToSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    const sheetUrl = getSheetUrl(section.key);
    const sheetId = getSheetId(sheetUrl);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/visaDetail/push-sheet`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content, sheetUrl, sheetId }),
      });
      if (!res.ok) {
        const text = await res.text();
        console.error("Push response:", res.status, text);
        setMessage(`Push failed (${res.status}): ${text.substring(0, 200)}`);
        return;
      }
      const data = await res.json();
      setMessage(data.success ? "Pushed to Google Sheet successfully!" : data.error || "Push failed.");
    } catch (err) {
      console.error("Push error:", err);
      setMessage("Push error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePullFromSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    const sheetUrl = getSheetUrl(section.key);
    const sheetId = getSheetId(sheetUrl);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/visaDetail/pull-sheet`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ sheetUrl, sheetId }),
      });
      const data = await res.json();
      if (data.success) {
        setContent(data.content);
        setMessage("Pulled from Google Sheet. Review changes and click Publish to go live.");
      } else {
        setMessage(data.error || "Pull failed.");
      }
    } catch (err) {
      setMessage("Pull error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Resource Group handlers (existing) ──────────────────────────────

  const handleResourceLinkChange = (groupIndex, linkIndex, key, value) => {
    setContent((prev) => {
      const resources = [...(prev.resources || [])];
      const links = [...(resources[groupIndex].links || [])];
      links[linkIndex] = { ...links[linkIndex], [key]: value };
      resources[groupIndex] = { ...resources[groupIndex], links };
      return { ...prev, resources };
    });
  };

  const handleAddResourceLink = (groupIndex) => {
    setContent((prev) => {
      const resources = [...(prev.resources || [])];
      const links = [...(resources[groupIndex].links || []), { label: "", href: "", desc: "" }];
      resources[groupIndex] = { ...resources[groupIndex], links };
      return { ...prev, resources };
    });
  };

  const handleDeleteResourceLink = (groupIndex, linkIndex) => {
    setContent((prev) => {
      const resources = [...(prev.resources || [])];
      const links = resources[groupIndex].links.filter((_, i) => i !== linkIndex);
      resources[groupIndex] = { ...resources[groupIndex], links };
      return { ...prev, resources };
    });
  };

  const handleAddResourceLinkBefore = (groupIndex, linkIndex) => {
    setContent((prev) => {
      const resources = [...(prev.resources || [])];
      const links = [...(resources[groupIndex].links || [])];
      links.splice(linkIndex, 0, { label: "", href: "", desc: "" });
      resources[groupIndex] = { ...resources[groupIndex], links };
      return { ...prev, resources };
    });
  };

  const handleAddResourceLinkAfter = (groupIndex, linkIndex) => {
    setContent((prev) => {
      const resources = [...(prev.resources || [])];
      const links = [...(resources[groupIndex].links || [])];
      links.splice(linkIndex + 1, 0, { label: "", href: "", desc: "" });
      resources[groupIndex] = { ...resources[groupIndex], links };
      return { ...prev, resources };
    });
  };

  const handleMoveResourceLinkUp = (groupIndex, linkIndex) => {
    if (linkIndex === 0) return;
    setContent((prev) => {
      const resources = [...(prev.resources || [])];
      const links = [...(resources[groupIndex].links || [])];
      [links[linkIndex - 1], links[linkIndex]] = [links[linkIndex], links[linkIndex - 1]];
      resources[groupIndex] = { ...resources[groupIndex], links };
      return { ...prev, resources };
    });
  };

  const handleMoveResourceLinkDown = (groupIndex, linkIndex) => {
    setContent((prev) => {
      const resources = [...(prev.resources || [])];
      const links = [...(resources[groupIndex].links || [])];
      if (linkIndex >= links.length - 1) return prev;
      [links[linkIndex], links[linkIndex + 1]] = [links[linkIndex + 1], links[linkIndex]];
      resources[groupIndex] = { ...resources[groupIndex], links };
      return { ...prev, resources };
    });
  };

  // ── Generic Array handlers ───────────────────────────────────────────

  const handleArrayDeleteRow = (field, index) => {
    setContent((prev) => {
      const arr = (prev[field] || []).filter((_, i) => i !== index);
      return { ...prev, [field]: arr };
    });
  };

  const handleArrayMoveUp = (field, index) => {
    if (index === 0) return;
    setContent((prev) => {
      const arr = [...(prev[field] || [])];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      return { ...prev, [field]: arr };
    });
  };

  const handleArrayMoveDown = (field, index) => {
    setContent((prev) => {
      const arr = [...(prev[field] || [])];
      if (index >= arr.length - 1) return prev;
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      return { ...prev, [field]: arr };
    });
  };

  // ── Row Form Modal handlers ─────────────────────────────────────────

  const handleOpenAddForm = (field, positionIndex) => {
    const columns = VISA_DETAIL_COLUMNS[field];
    const emptyRow = {};
    if (columns) {
      for (const col of columns) {
        emptyRow[col.key] = col.type === "tags" ? [] : "";
      }
    }
    setEditingField(field);
    setEditingIndex(null);
    setEditingItem(emptyRow);
    setTagInputValue("");
    setInsertAtPosition(positionIndex ?? null);
    setShowRowForm(true);
  };

  const handleOpenEditForm = (field, index) => {
    const item = content?.[field]?.[index];
    if (!item) return;
    setEditingField(field);
    setEditingIndex(index);
    setEditingItem({ ...item });
    setTagInputValue("");
    setShowRowForm(true);
  };

  const handleCloseRowForm = () => {
    setShowRowForm(false);
    setEditingField(null);
    setEditingIndex(null);
    setEditingItem({});
    setTagInputValue("");
    setInsertAtPosition(null);
  };

  const handleRowFormFieldChange = (key, value) => {
    setEditingItem((prev) => ({ ...prev, [key]: value }));
  };

  const handleRowFormTagAdd = (tagKey) => {
    if (!tagInputValue.trim()) return;
    setEditingItem((prev) => {
      const tags = [...(prev[tagKey] || [])];
      if (!tags.includes(tagInputValue.trim())) {
        tags.push(tagInputValue.trim());
      }
      return { ...prev, [tagKey]: tags };
    });
    setTagInputValue("");
  };

  const handleRowFormTagRemove = (tagKey, tag) => {
    setEditingItem((prev) => ({
      ...prev,
      [tagKey]: (prev[tagKey] || []).filter((t) => t !== tag),
    }));
  };

  const handleSaveRowForm = () => {
    const field = editingField;
    const position = insertAtPosition;
    setContent((prev) => {
      const arr = [...(prev[field] || [])];
      const newItem = { ...editingItem };
      if (field === "resources") {
        const title = (newItem.title || "").trim();
        newItem.id = title.toLowerCase().replace(/\s+/g, "-");
        newItem.links = [];
      }
      if (editingIndex === null) {
        // New row
        if (position !== null && position >= 0) {
          arr.splice(position, 0, newItem);
        } else {
          arr.push(newItem);
        }
      } else {
        // Update existing row
        arr[editingIndex] = newItem;
      }
      return { ...prev, [field]: arr };
    });
    handleCloseRowForm();
  };

  // ── Submit ──────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    const dupes = findDuplicates(content, VISA_DETAIL_DUPLICATE_CHECK);
    if (dupes.length > 0) {
      setDuplicateInfo(dupes);
      setShowDuplicateModal(true);
      return;
    }
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleConfirmSubmit = async () => {
    if (!noteText.trim()) return;
    setShowNoteModal(false);
    await doSubmit(noteText.trim());
  };

  const handleContinueAnyway = async () => {
    setShowDuplicateModal(false);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleRemoveDuplicates = async () => {
    setDuplicateActionLoading(true);
    const cleanContent = JSON.parse(JSON.stringify(content));
    const sorted = [...duplicateInfo].sort((a, b) => {
      if (a.sectionIndex !== undefined && b.sectionIndex !== undefined) {
        if (a.sectionIndex !== b.sectionIndex) return b.sectionIndex - a.sectionIndex;
      }
      return b.duplicateIndex - a.duplicateIndex;
    });
    for (const dup of sorted) {
      if (dup.nestedField) {
        const sections = cleanContent[dup.field] || [];
        const section = sections[dup.sectionIndex];
        if (!section) continue;
        const rows = [...(section[dup.nestedField] || [])];
        if (rows[dup.duplicateIndex]) {
          rows.splice(dup.duplicateIndex, 1);
          section[dup.nestedField] = rows;
        }
      } else {
        const arr = [...(cleanContent[dup.field] || [])];
        if (arr[dup.duplicateIndex]) {
          arr.splice(dup.duplicateIndex, 1);
          cleanContent[dup.field] = arr;
        }
      }
    }
    setContent(cleanContent);
    setShowDuplicateModal(false);
    setDuplicateActionLoading(false);
    snapshotRef.current = JSON.stringify(cleanContent);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit("", cleanContent);
  };

  const doSubmit = async (submitterNote, overrideContent) => {
    setMessage("");
    setSaving(true);

    const token = sessionStorage.getItem("tvc_token");

    const submitPayload = overrideContent || { ...content };

    try {
      const res = await fetch(`${API_BASE_URL}/content/submit-change`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          description: getContentUpdateDescription(section.label),
          changes: {
            type: "content_update",
            sectionKey: section.key,
            content: submitPayload,
            ...(submitterNote ? { submitterNote } : {}),
          },
          sectionKey: section.key,
        }),
      });

      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { error: text }; }
      if (res.ok) {
        const role = user?.role || "Staff";
        setSuccessTitle(role === "Admin" ? "Changes Published" : "Request made");
        setShowSuccessOverlay(true);
        snapshotRef.current = JSON.stringify(content);
      } else {
        console.error("Submit failed:", res.status, text);
        setMessage(data.error || `Server error (${res.status})`);
      }
    } catch (err) {
      setMessage(`Network error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Simple inline field change handler (for states & countries) ────

  const handleSimpleFieldChange = (field, index, key, value) => {
    setContent((prev) => {
      const arr = [...(prev[field] || [])];
      arr[index] = { ...arr[index], [key]: value };
      return { ...prev, [field]: arr };
    });
  };

  // ── Renderers ───────────────────────────────────────────────────────

  const renderResourceEditor = (group, groupIndex) => {
    const idxKey = String(groupIndex);
    const cardSearch = linkSearch[idxKey] || "";
    const cardSearchOpen = linkSearchOpen[idxKey] || false;
    const links = group.links || [];
    const filteredLinks = cardSearch.trim()
      ? links.filter((link) =>
          [link.label, link.href, link.desc].some(
            (v) => v && v.toLowerCase().includes(cardSearch.trim().toLowerCase())
          )
        )
      : links;
    return (
      <div key={groupIndex} className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">
            {group.title || "Group Title"}
          </h4>
          <div className="AdminSectionHeaderActions">
            <div className={`AdminPillSearchWrap ${cardSearchOpen ? 'is-open' : ''}`}>
              <span className="AdminPillSearchIcon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              <input
                ref={(el) => { linkSearchRefs.current[idxKey] = el; }}
                type="text"
                className="AdminPillSearch"
                placeholder="Search links..."
                value={cardSearch}
                onChange={(e) => setLinkSearch((prev) => ({ ...prev, [idxKey]: e.target.value }))}
                onBlur={() => setTimeout(() => setLinkSearchOpen((prev) => ({ ...prev, [idxKey]: false })), 200)}
              />
              {!cardSearchOpen && (
                <button className="AdminPillSearchOverlay" onClick={() => setLinkSearchOpen((prev) => ({ ...prev, [idxKey]: true }))} type="button" aria-label="Search" />
              )}
            </div>
            <button
              className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
              onClick={() => handleAddResourceLink(groupIndex)}
            >
              + Add Link
            </button>
          </div>
        </div>
        <TableWithScrollButtons>
          <table className="AdminTable AdminTableEditor">
            <thead>
              <tr>
                <th className="AdminThOrder">Order</th>
                <th>Label</th>
                <th>URL</th>
                <th>Description</th>
                <th className="AdminThActions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLinks.map((link) => {
                const linkIndex = links.indexOf(link);
                return (
                <tr
                  key={linkIndex}
                  draggable="true"
                  onDragStart={(e) => {
                    dragRef.current = { type: "resourceLink", groupIndex, fromIndex: linkIndex };
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                    e.currentTarget.style.opacity = "0.4";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    e.currentTarget.style.outline = "2px dashed #3b82f6";
                    e.currentTarget.style.outlineOffset = "-2px";
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.style.outline = "";
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    if (!dragRef.current || dragRef.current.type !== "resourceLink" || dragRef.current.groupIndex !== groupIndex) { dragRef.current = null; return; }
                    const { fromIndex } = dragRef.current;
                    if (fromIndex === linkIndex) { dragRef.current = null; return; }
                    setContent((prev) => {
                      const resources = [...(prev.resources || [])];
                      const links = [...(resources[groupIndex].links || [])];
                      const [moved] = links.splice(fromIndex, 1);
                      links.splice(linkIndex, 0, moved);
                      resources[groupIndex] = { ...resources[groupIndex], links };
                      return { ...prev, resources };
                    });
                    dragRef.current = null;
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    dragRef.current = null;
                  }}
                >
                  <td>
                    <div className="AdminOrderBtns">
                      <button
                        className="AdminMoveBtn"
                        onClick={() => handleMoveResourceLinkUp(groupIndex, linkIndex)}
                        disabled={linkIndex === 0}
                        title="Move up"
                      >
                        &#8593;
                      </button>
                      <button
                        className="AdminMoveBtn"
                        onClick={() => handleMoveResourceLinkDown(groupIndex, linkIndex)}
                        disabled={linkIndex === (links.length - 1)}
                        title="Move down"
                      >
                        &#8595;
                      </button>
                    </div>
                  </td>
                  <td>
                    <input
                      className="AdminInput AdminTableInput"
                      value={link.label || ""}
                      onChange={(e) => handleResourceLinkChange(groupIndex, linkIndex, "label", e.target.value)}
                      placeholder="Label"
                    />
                  </td>
                  <td>
                    <input
                      className="AdminInput AdminTableInput"
                      value={link.href || ""}
                      onChange={(e) => handleResourceLinkChange(groupIndex, linkIndex, "href", e.target.value)}
                      placeholder="URL"
                    />
                  </td>
                  <td>
                    <input
                      className="AdminInput AdminTableInput"
                      value={link.desc || ""}
                      onChange={(e) => handleResourceLinkChange(groupIndex, linkIndex, "desc", e.target.value)}
                      placeholder="Description"
                    />
                  </td>
                  <td>
                    <div className="AdminActionBtns">
                      <button
                        className="AdminIconBtn AdminIconBtnBlue"
                        onClick={() => handleAddResourceLinkBefore(groupIndex, linkIndex)}
                        title="Add row before"
                      >
                        &#9650;+
                      </button>
                      <button
                        className="AdminIconBtn AdminIconBtnBlue"
                        onClick={() => handleAddResourceLinkAfter(groupIndex, linkIndex)}
                        title="Add row after"
                      >
                        &#9660;+
                      </button>
                      <button
                        className="AdminIconBtn AdminIconBtnRed"
                        onClick={() => handleDeleteResourceLink(groupIndex, linkIndex)}
                        title="Delete row"
                      >
                        &#10005;
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </TableWithScrollButtons>
        {links.length === 0 && (
          <div className="AdminEmptyState">{VISA_DETAIL_MESSAGES.emptyArray}</div>
        )}
        {links.length > 0 && cardSearch.trim() && filteredLinks.length === 0 && (
          <div className="AdminEmptyState">No results found for "{cardSearch}"</div>
        )}
      </div>
    );
  };

  const renderArrayEditor = (field, columns) => {
    const items = content?.[field] || [];
    const colCount = columns.length;
    const isWide = colCount > 6;

    // Helper to display a cell value
    const displayValue = (item, col) => {
      const val = item[col.key];
      if (col.type === "tags") {
        const arr = val || [];
        return arr.length > 0 ? arr.slice(0, 3).join(", ") + (arr.length > 3 ? ` +${arr.length - 3}` : "") : "—";
      }
      if (!val || val === "") return "—";
      return val;
    };

    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">
              {field === "visaOffices" ? "Visa Offices & FRROs" :
             field === "embassies" ? "Embassies & Consulates" :
             field === "visaForms" ? "Visa Form" :
             field === "location" ? "Location (States & Countries)" :
             field.replace(/([A-Z])/g, " $1").trim()}
          </h4>
          <div className="AdminSectionHeaderActions">
            <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
              <span className="AdminPillSearchIcon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              <input
                ref={searchRef}
                type="text"
                className="AdminPillSearch"
                placeholder="Search..."
                value={arraySearch}
                onChange={(e) => setArraySearch(e.target.value)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
              />
              {!searchOpen && (
                <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
              )}
            </div>
            <button
              className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
              onClick={() => handleOpenAddForm(field)}
            >
              + Add Row
            </button>
          </div>
        </div>
        <TableWithScrollButtons>
          <table className="AdminTable AdminTableEditor">
            <thead>
              <tr>
                <th className="AdminThOrder">Order</th>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th className="AdminThActions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items
                .map((item, idx) => ({ item, idx }))
                .filter(({ item }) => {
                  if (!arraySearch.trim()) return true;
                  return columns.some((col) => {
                    const val = col.type === "tags" ? (item[col.key] || []).join(" ") : item[col.key];
                    return String(val || "").toLowerCase().includes(arraySearch.trim().toLowerCase());
                  });
                })
                .map(({ item, idx }) => (
                <tr
                  key={idx}
                  draggable="true"
                  onDragStart={(e) => {
                    dragRef.current = { type: "array", field, fromIndex: idx };
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                    e.currentTarget.style.opacity = "0.4";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    e.currentTarget.style.outline = "2px dashed #3b82f6";
                    e.currentTarget.style.outlineOffset = "-2px";
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.style.outline = "";
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    if (!dragRef.current || dragRef.current.type !== "array" || dragRef.current.field !== field) { dragRef.current = null; return; }
                    const { fromIndex } = dragRef.current;
                    if (fromIndex === idx) { dragRef.current = null; return; }
                    setContent((prev) => {
                      const arr = [...(prev[field] || [])];
                      const [moved] = arr.splice(fromIndex, 1);
                      arr.splice(idx, 0, moved);
                      return { ...prev, [field]: arr };
                    });
                    dragRef.current = null;
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    dragRef.current = null;
                  }}
                >
                  <td>
                    <div className="AdminOrderBtns">
                      <button
                        className="AdminMoveBtn"
                        onClick={() => handleArrayMoveUp(field, idx)}
                        disabled={idx === 0}
                        title="Move up"
                      >
                        &#8593;
                      </button>
                      <button
                        className="AdminMoveBtn"
                        onClick={() => handleArrayMoveDown(field, idx)}
                        disabled={idx === items.length - 1}
                        title="Move down"
                      >
                        &#8595;
                      </button>
                      <button
                        className="AdminEditBtn"
                        onClick={() => handleOpenEditForm(field, idx)}
                        title="Edit row"
                      >
                        &#9998;
                      </button>
                    </div>
                  </td>
                  {columns.map((col) => (
                    <td key={col.key} className={`AdminTableCell ${isWide ? 'AdminMinW140' : 'AdminTableCellNonWide'}`}>
                      <span className="AdminTableCellText">{displayValue(item, col)}</span>
                    </td>
                  ))}
                  <td>
                    <div className="AdminActionBtns">
                      <button
                        className="AdminIconBtn AdminIconBtnBlue"
                        onClick={() => handleOpenAddForm(field, idx)}
                        title="Add row before"
                      >
                        &#9650;+
                      </button>
                      <button
                        className="AdminIconBtn AdminIconBtnBlue"
                        onClick={() => handleOpenAddForm(field, idx + 1)}
                        title="Add row after"
                      >
                        &#9660;+
                      </button>
                      <button
                        className="AdminIconBtn AdminIconBtnRed"
                        onClick={() => handleArrayDeleteRow(field, idx)}
                        title="Delete row"
                      >
                        &#10005;
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWithScrollButtons>
        {items.length === 0 && (
          <div className="AdminEmptyState">{VISA_DETAIL_MESSAGES.emptyArray}</div>
        )}
        {items.length > 0 && arraySearch.trim() && !items.some((item) =>
          columns.some((col) => {
            const val = col.type === "tags" ? (item[col.key] || []).join(" ") : item[col.key];
            return String(val || "").toLowerCase().includes(arraySearch.trim().toLowerCase());
          })
        ) && (
          <div className="AdminEmptyState">No results found for "{arraySearch}"</div>
        )}
      </div>
    );
  };

  const renderSimpleEditor = (field, label, searchValue, setSearchValue, searchIsOpen, setSearchIsOpen, searchRefEl) => {
    const items = content?.[field] || [];
    const filteredItems = items.filter((item) => {
      if (!searchValue.trim()) return true;
      return (item.name || "").toLowerCase().includes(searchValue.trim().toLowerCase());
    });

    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">{label}</h4>
          <div className="AdminSectionHeaderActions">
            <div className={`AdminPillSearchWrap ${searchIsOpen ? 'is-open' : ''}`}>
              <span className="AdminPillSearchIcon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              <input
                ref={searchRefEl}
                type="text"
                className="AdminPillSearch"
                placeholder="Search..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onBlur={() => setTimeout(() => setSearchIsOpen(false), 200)}
              />
              {!searchIsOpen && (
                <button className="AdminPillSearchOverlay" onClick={() => setSearchIsOpen(true)} type="button" aria-label="Search" />
              )}
            </div>
            <button
              className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
              onClick={() => handleOpenAddForm(field)}
            >
              + Add Row
            </button>
          </div>
        </div>
        <TableWithScrollButtons>
          <table className="AdminTable AdminTableEditor">
            <thead>
              <tr>
                <th className="AdminThOrder">Order</th>
                <th>Name</th>
                <th className="AdminThActions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item, idx) => (
                <tr
                  key={idx}
                  draggable="true"
                  onDragStart={(e) => {
                    dragRef.current = { type: "array", field, fromIndex: idx };
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                    e.currentTarget.style.opacity = "0.4";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    e.currentTarget.style.outline = "2px dashed #3b82f6";
                    e.currentTarget.style.outlineOffset = "-2px";
                  }}
                  onDragLeave={(e) => { e.currentTarget.style.outline = ""; }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    if (!dragRef.current || dragRef.current.type !== "array" || dragRef.current.field !== field) { dragRef.current = null; return; }
                    const { fromIndex } = dragRef.current;
                    if (fromIndex === idx) { dragRef.current = null; return; }
                    setContent((prev) => {
                      const arr = [...(prev[field] || [])];
                      const [moved] = arr.splice(fromIndex, 1);
                      arr.splice(idx, 0, moved);
                      return { ...prev, [field]: arr };
                    });
                    dragRef.current = null;
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    dragRef.current = null;
                  }}
                >
                  <td>
                    <div className="AdminOrderBtns">
                      <button
                        className="AdminMoveBtn"
                        onClick={() => handleArrayMoveUp(field, idx)}
                        disabled={idx === 0}
                        title="Move up"
                      >
                        &#8593;
                      </button>
                      <button
                        className="AdminMoveBtn"
                        onClick={() => handleArrayMoveDown(field, idx)}
                        disabled={idx === items.length - 1}
                        title="Move down"
                      >
                        &#8595;
                      </button>
                    </div>
                  </td>
                  <td>
                    <input
                      className="AdminInput AdminTableInput"
                      value={item.name || ""}
                      onChange={(e) => handleSimpleFieldChange(field, idx, "name", e.target.value)}
                      placeholder="Enter name..."
                    />
                  </td>
                  <td>
                    <div className="AdminActionBtns">
                      <button
                        className="AdminIconBtn AdminIconBtnBlue"
                        onClick={() => handleOpenAddForm(field, idx)}
                        title="Add row before"
                      >
                        &#9650;+
                      </button>
                      <button
                        className="AdminIconBtn AdminIconBtnBlue"
                        onClick={() => handleOpenAddForm(field, idx + 1)}
                        title="Add row after"
                      >
                        &#9660;+
                      </button>
                      <button
                        className="AdminIconBtn AdminIconBtnRed"
                        onClick={() => handleArrayDeleteRow(field, idx)}
                        title="Delete row"
                      >
                        &#10005;
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWithScrollButtons>
        {items.length === 0 && (
          <div className="AdminEmptyState">{VISA_DETAIL_MESSAGES.emptyArray}</div>
        )}
        {items.length > 0 && searchValue.trim() && filteredItems.length === 0 && (
          <div className="AdminEmptyState">No results found for "{searchValue}"</div>
        )}
      </div>
    );
  };

  // ── Loading / Empty States ──────────────────────────────────────────

  if (loading) {
    return <div className="AdminEmptyState">{VISA_DETAIL_MESSAGES.loading}</div>;
  }

  if (!content) {
    return (
      <div>
        <button className="AdminButton AdminButtonSmall AdminButtonSecondary AdminBackBtn" onClick={onBack}>
          &larr; Back to Content Manager
        </button>
        <div className="AdminEmptyState">{VISA_DETAIL_MESSAGES.noContent}</div>
      </div>
    );
  }

  const isDirty = JSON.stringify(content) !== snapshotRef.current;

  // ── Main Render ─────────────────────────────────────────────────────

  return (
    <div className="AdminCard">
      <div className="AdminCardHeader">
        <h3 className="AdminFooterH3">
          Edit Visa Detail Content
          {activeTab !== "data" && activeTab === "location" && (
            <span className="AdminCountBadge">{(content?.states?.length || 0) + (content?.countries?.length || 0)}</span>
          )}
          {activeTab !== "data" && activeTab !== "location" && Array.isArray(content?.[activeTab]) && (
            <span className="AdminCountBadge">{content[activeTab].length}</span>
          )}
        </h3>
        <div className="AdminHeaderActionsRow">
          <button className="AdminButton AdminButtonSmall AdminSubmitBtn AdminNowrap" onClick={handleSubmit} disabled={!isDirty || saving}>
            {saving ? "Publishing..." : getSubmitButtonLabel(user?.role)}
          </button>
          <button
            className="AdminButton AdminButtonSmall AdminSheetBtn"
            onClick={handleOpenSheet}
          >
            Google Sheet
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePushToSheet} disabled={loading} title="Push to Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePullFromSheet} disabled={loading} title="Pull from Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
        </div>
      </div>

      {message && (
        <p className={`AdminMsgBox ${message.includes("success") ? "AdminSuccess" : "AdminError"}`}>
          {message}
        </p>
      )}

      <div
        className="AdminTabBar"
        ref={tabBarRef}
        style={{ display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0", marginBottom: 16 }}
      >
        {VISA_DETAIL_TABS.map((tab) => (
          <button
            key={tab.id}
            draggable
            onDragStart={(e) => { tabDragRef.current = tab.id; e.target.style.opacity = '0.5'; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const fromId = tabDragRef.current;
              if (!fromId || fromId === tab.id) return;
              setActiveTab(tab.id);
            }}
            onDragEnd={(e) => { e.target.style.opacity = '1'; tabDragRef.current = null; }}
            className={`AdminTabBtn ${activeTab === tab.id ? "AdminTabBtnActive" : ""}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 20px", border: "none", background: activeTab === tab.id ? "#1B2A4A" : "transparent",
              color: activeTab === tab.id ? "#fff" : "#64748b", cursor: "grab", fontSize: 14, fontWeight: 600,
              borderRadius: "8px 8px 0 0", transition: "all 0.2s",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "resources" && (
        <>
          {/* Groups table — same style as the other sections */}
          {renderArrayEditor("resources", VISA_DETAIL_COLUMNS.resources)}
          {/* Cards below the table for managing links */}
          {(content.resources || []).map((group, idx) => renderResourceEditor(group, idx))}
        </>
      )}

      {activeTab === "visaOffices" && (
        <>
          {renderSimpleEditor("visaOfficeTypes", "Types", visaOfficeTypeSearch, setVisaOfficeTypeSearch, visaOfficeTypeSearchOpen, setVisaOfficeTypeSearchOpen, visaOfficeTypeSearchRef)}
          {renderArrayEditor("visaOffices", VISA_DETAIL_COLUMNS.visaOffices)}
        </>
      )}

      {activeTab === "embassies" && (
        <>
          {renderSimpleEditor("embassyTypes", "Types", embassyTypeSearch, setEmbassyTypeSearch, embassyTypeSearchOpen, setEmbassyTypeSearchOpen, embassyTypeSearchRef)}
          {renderArrayEditor("embassies", VISA_DETAIL_COLUMNS.embassies)}
        </>
      )}

      {activeTab === "visaForms" && renderArrayEditor("visaForms", VISA_DETAIL_COLUMNS.visaForms)}

      {activeTab === "location" && (
        <>
          {renderSimpleEditor("states", "State", stateSearch, setStateSearch, stateSearchOpen, setStateSearchOpen, stateSearchRef)}
          {renderSimpleEditor("countries", "Country", countrySearch, setCountrySearch, countrySearchOpen, setCountrySearchOpen, countrySearchRef)}
        </>
      )}

      {activeTab === "data" && (
        <div className="AdminCard">
          <div className="AdminDataHeader">
            <h4 className="AdminSectionTitle">
              Data
            </h4>
          </div>
          <div className="AdminDataInfo">
            <div>
              <span className="AdminDataLabel">Last Updated</span>
              <p className="AdminDataValue">
                {sectionInfo?.updatedAt ? new Date(sectionInfo.updatedAt).toLocaleString() : "Not published"}
              </p>
            </div>
            <div>
              <span className="AdminDataLabel">Section Key</span>
              <p className="AdminDataValue">
                {section?.key || "visaDetail"}
              </p>
            </div>
          </div>
          <textarea
            className="AdminJsonEditor"
            value={JSON.stringify(content, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                setContent(parsed);
                setMessage("");
              } catch {
                setMessage(VISA_DETAIL_MESSAGES.invalidJson);
              }
            }}
          />
        </div>
      )}

      {showRowForm && (
        <div className="AdminModalOverlayFixed">
          <div className="AdminModalContentBox">
            <button
              onClick={handleCloseRowForm}
              className="AdminModalCloseBtn"
              title="Close"
            >
              &#10005;
            </button>
            <h3 className="AdminModalFormTitle">
              {editingIndex === null ? 'Add' : 'Edit'}{" "}
               {editingField === "resources" ? "Resource Group" :
               editingField === "visaOffices" ? "Visa Office" :
               editingField === "embassies" ? "Embassy / Consulate" :
               editingField === "visaForms" ? "Visa Form" :
               editingField === "states" ? "State" :
               editingField === "countries" ? "Country" :
               editingField === "visaOfficeTypes" ? "Visa Office Type" :
               editingField === "embassyTypes" ? "Embassy Type" :
               editingField === "location" ? "Location Entry" :
               "Row"}
            </h3>
            <p className="AdminModalFormSubtitle">
              Fill in the details and press Continue to add it to the table.
            </p>
            <div className="AdminModalFormGrid">
              {VISA_DETAIL_COLUMNS[editingField]?.map((col) => {
                const isResource = editingField === "resources";
                return (
                  <div key={col.key} className={col.type === "tags" ? "AdminModalFormGridFull" : isResource ? "AdminModalFormGridCenter" : ""}>
                    <label className="AdminModalFormLabel">
                      {col.label}
                    </label>
                    {col.type === "tags" ? (
                      <div className={isResource ? "AdminModalFormInputCentered" : ""}>
                        <div className="AdminModalFormTagsWrap">
                          {(editingItem[col.key] || []).map((tag) => (
                            <span
                              key={tag}
                              className="AdminModalFormTag"
                              onClick={() => handleRowFormTagRemove(col.key, tag)}
                              title="Click to remove"
                            >
                              {tag} &times;
                            </span>
                          ))}
                        </div>
                        <div className="AdminModalFormTagInputRow">
                          <input
                            className="AdminInput AdminModalFormTagInput"
                            placeholder="Type a tag and press Add..."
                            value={tagInputValue}
                            onChange={(e) => setTagInputValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleRowFormTagAdd(col.key);
                              }
                            }}
                          />
                          <button
                            className="AdminButton AdminButtonSmall AdminButtonSecondary AdminNowrap"
                            onClick={() => handleRowFormTagAdd(col.key)}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    ) : col.key === "state" || col.key === "country" || col.key === "office_type" || col.key === "embassy_type" ? (
                      <div className={isResource ? "AdminModalFormInputCentered" : ""}>
                        <input
                          className="AdminInput AdminModalFormInput"
                          list={`${editingField}-${col.key}-list`}
                          value={editingItem[col.key] || ""}
                          onChange={(e) => handleRowFormFieldChange(col.key, e.target.value)}
                          placeholder={`Search or type ${col.label}`}
                        />
                        <datalist id={`${editingField}-${col.key}-list`}>
                          {(
                            col.key === "state" ? (content?.states || []) :
                            col.key === "country" ? (content?.countries || []) :
                            col.key === "office_type" ? (content?.visaOfficeTypes || []) :
                            (content?.embassyTypes || [])
                          ).map((opt) => (
                            <option key={opt.name} value={opt.name} />
                          ))}
                        </datalist>
                      </div>
                    ) : (
                      <div className={isResource ? "AdminModalFormInputCentered" : ""}>
                        <input
                          className="AdminInput AdminModalFormInput"
                          value={editingItem[col.key] || ""}
                          onChange={(e) => handleRowFormFieldChange(col.key, e.target.value)}
                          placeholder={col.label}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="AdminModalBtnRow">
              <button
                className="AdminButton AdminButtonSmall"
                onClick={handleSaveRowForm}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showNoteModal && (
        <div className="AdminModalOverlayFixed">
          <div className="AdminModalContentBoxNarrow">
            <h3>Add a note for the approver</h3>
            <textarea
              className="AdminNoteTextarea"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Describe your changes..."
            />
            <div className="AdminNoteModalRow">
              <button
                className="AdminButton AdminButtonSmall AdminButtonSecondary"
                onClick={() => setShowNoteModal(false)}
              >
                Cancel
              </button>
              <button
                className="AdminButton AdminButtonSmall"
                onClick={handleConfirmSubmit}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateModal && duplicateInfo.length > 0 && (
        <div className="AdminModal">
          <div className="AdminModalContent AdminModalMaxW520" style={{position:"relative"}}>
            <button onClick={() => setShowDuplicateModal(false)} style={{ position: "absolute", top: 8, right: 12, background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#64748b", lineHeight: 1 }} title="Close">&times;</button>
            <div className="AdminText36 AdminTaCenter AdminM0B8">&#9888;</div>
            <h3 className="AdminModalTitle AdminM0B4 AdminTaCenter">Duplicate Entries Detected</h3>
            <p className="AdminText13 AdminTextMuted AdminM0B16 AdminTaCenter">
              The following duplicate entries were found. Choose how to proceed.
            </p>
            <div className="AdminDupWarningBox">
              {(() => {
                const byTab = {};
                for (const d of duplicateInfo) {
                  if (!byTab[d.tabId]) byTab[d.tabId] = { label: d.tabLabel, items: [] };
                  const prefix = d.sectionHeading ? `[${d.sectionHeading}] ` : "";
                  const dispVal = d.duplicateValue || "Row #" + (d.duplicateIndex + 1);
                  byTab[d.tabId].items.push(prefix + dispVal);
                }
                return Object.entries(byTab).map(([tabId, info]) => (
                  <div key={tabId} className="AdminDupItem">
                    <strong className="AdminTextDark">{info.label}</strong>
                    <span className="AdminTextSlate"> &mdash; Duplicate{info.items.length > 1 ? "s" : ""}: </span>
                    <span className="AdminTextDark">{info.items.join(", ")}</span>
                  </div>
                ));
              })()}
            </div>
            <div className="AdminFlexCenterG12">
              <button
                className="AdminButton AdminSubmitBtn AdminBtnDanger"
                onClick={handleRemoveDuplicates}
                disabled={duplicateActionLoading}
              >
                {duplicateActionLoading ? "Removing..." : "Remove Duplicate Rows"}
              </button>
              <button
                className="AdminButton AdminSubmitBtn"
                onClick={handleContinueAnyway}
                disabled={duplicateActionLoading}
              >
                Continue Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessOverlay && (
        <div className="AdminModal" onClick={() => setShowSuccessOverlay(false)}>
          <div className="AdminModalContent AdminModalMaxW400" onClick={e => e.stopPropagation()}>
            <div className="AdminText48">
              {successTitle === "Changes Published" ? "\u2705" : "\uD83D\uDCEB"}
            </div>
            <h3 className="AdminModalTitle AdminMb4">{successTitle}</h3>
            <p className="AdminText13 AdminTextMuted AdminM0B20">
              {successTitle === "Changes Published" ? "Your content changes have been published live." : "Your change request has been submitted for approval."}
            </p>
            <div className="AdminFlexCenter">
              <button
                className="AdminButton AdminSubmitBtn"
                onClick={() => setShowSuccessOverlay(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminVisaDetailEditor;
